import React from "react";
import { MenuItem } from "@mui/material";
import type { StyledComponentBase } from "@/components/component-base.types";
import classNames from "classnames";
import styles from "./dropdown-menu-item.module.scss";

/**
 * Represents a single item in a dropdown menu.
 * value - The value associated with the menu item (string or number).
 * text - The display text for the menu item.
 * className - Optional additional class names for styling.
 */
export interface IDropdownMenuItem {
  value: string | number;
  text: string;
  className?: string;
}

/**
 * Props for DropdownMenuItem component.
 * isSelected - Whether the item is currently selected.
 * onClick - Handler called when the item is clicked, receives the value.
 * showSelectedItemCheckmark - If true, shows a checkmark for selected item.
 */
interface IDropdownMenuItemProps extends StyledComponentBase, IDropdownMenuItem {
  isSelected: boolean;
  onClick: (value: string | number) => void;
  showSelectedItemCheckmark?: boolean;
}

/**
 * A styled menu dropdown item
 * @param iProps the component props
 * @returns JSX representing a styled menu dropdown item
 */
export const DropdownMenuItem = (iProps: IDropdownMenuItemProps) => {
  const {
    text,
    isSelected: selected,
    value,
    className,
    onClick,
    showSelectedItemCheckmark,
  } = iProps;
  return (
    <MenuItem
      key={value}
      className={classNames(
        styles.dropdownMenuItem,
        { [styles.selected]: showSelectedItemCheckmark && selected },
        { [styles.selectable]: showSelectedItemCheckmark },
        className
      )}
      onClick={() => onClick(value)}
    >
      {text}
    </MenuItem>
  );
};
